import { useShallow } from 'zustand/react/shallow';

import { useReaderStore } from './slice';

import type { TChapterData } from '@/app/(app)/reader/types';

// 稳定的空数组引用，避免 ?? [] 在每次 selector 调用时创建新引用导致 useSyncExternalStore 无限循环
const EMPTY_CHAPTER_DATA: TChapterData = [];

// ── 纯同步 store actions ────────────────────────────────────────
export const useReaderActions = () =>
    useReaderStore(
        useShallow(state => ({
            initReaderConfig: state.initReaderConfig,
            setTheme: state.setTheme,
            setFontSize: state.setFontSize,
            setBrightness: state.setBrightness,
            setInitReady: state.setInitReady,
            setChapterDetailReady: state.setChapterDetailReady,
            setShowMenu: state.setShowMenu,
            setShowCatalog: state.setShowCatalog,
            initDramaInfo: state.initDramaInfo,
            setCatalogList: state.setCatalogList,
            resetRenderList: state.resetRenderList,
            appendChapter: state.appendChapter,
            prependChapter: state.prependChapter,
            clearUnHandlePreId: state.clearUnHandlePreId,
            clearUnHandleTrimHeadIds: state.clearUnHandleTrimHeadIds,
            setAnchorChapterId: state.setAnchorChapterId,
            addLoadingChapter: state.addLoadingChapter,
            removeLoadingChapter: state.removeLoadingChapter,
            setBookDetail: state.setBookDetail,
            setUserAsset: state.setUserAsset,
            setBookDetailPromise: state.setBookDetailPromise,
            createBookDetailReadyPromise: state.createBookDetailReadyPromise,
            resolveBookDetailReadyPromise: state.resolveBookDetailReadyPromise,
            syncCacheLockStatus: state.syncCacheLockStatus,
            syncRenderList: state.syncRenderList,
            updateChapterRenderItem: state.updateChapterRenderItem,
            setPurchaseLoading: state.setPurchaseLoading,
            setPurchaseResultMap: state.setPurchaseResultMap,
            setPendingAutoUnlockToast: state.setPendingAutoUnlockToast,
            consumePendingAutoUnlockToast: state.consumePendingAutoUnlockToast,
            setBookDetailLoading: state.setBookDetailLoading,
            resetAllData: state.resetAllData,
        }))
    );

// ── 外观配置（主题 + 字号 + 亮度） ────────────────────────────────────
export const useReaderAppearance = () =>
    useReaderStore(
        useShallow(state => ({
            theme: state.theme,
            fontSize: state.fontSize,
            brightness: state.brightness,
        }))
    );

// ── 初始来源 ID（URL 读取，仅初始化写入一次） ──────────────────
export const useReaderDramaInfo = () =>
    useReaderStore(
        useShallow(state => ({
            dramaId: state.dramaId,
            chapterId: state.chapterId,
        }))
    );

/**
 * 查找 Buffer 中当前锚点章节的原始数据。
 * 把选锚点的逻辑抽出来，便于多个 selector 复用并在回退场景返回稳定值。
 */
const pickAnchorBookData = (state: ReturnType<typeof useReaderStore.getState>) => {
    const anchor = state.chapterRenderList.find(c => c.chapterId === state.anchorChapterId);
    return anchor?.bookData;
};

// ── 当前锚点章节内容（保持对外接口不变） ───────────────────────
export const useReaderChapter = () =>
    useReaderStore(
        useShallow(state => {
            const book = pickAnchorBookData(state);
            return {
                chapterId: book?.chapterId ?? '',
                chapterTitle: book?.title ?? '',
                chapterData: book?.chapterData ?? EMPTY_CHAPTER_DATA,
                preChapterId: book?.preChapterId ?? '',
                nextChapterId: book?.nextChapterId ?? '',
                needPay: !book?.isFree && book?.isLocked,
                isFree: book?.isFree,
            };
        })
    );

// ── 书籍基本信息（优先取锚点章节，空时取 Buffer 任一章节） ─────
export const useReaderBookInfo = () =>
    useReaderStore(
        useShallow(state => {
            return {
                bookId: state.bookDetail?.dramaId ?? '',
                bookName: state.bookDetail?.title ?? '',
                coverUrl: state.bookDetail?.coverUrl ?? '',
                catalogList: state.catalogList,
            };
        })
    );

// ── 无限滚动渲染区相关 selector ──────────────────────────────
export const useChapterRenderList = () => useReaderStore(state => state.chapterRenderList);
export const useAnchorChapterId = () => useReaderStore(state => state.anchorChapterId);
export const useUnHandlePreId = () => useReaderStore(state => state.unHandlePreId);
export const useUnHandleTrimHeadIds = () => useReaderStore(state => state.unHandleTrimHeadIds);
export const useLoadingChapterIds = () => useReaderStore(state => state.loadingChapterIds);

// ── UI 显隐状态 ─────────────────────────────────────────────────
export const useReaderShowMenu = () => useReaderStore(state => state.showMenu);
export const useReaderShowCatalog = () => useReaderStore(state => state.showCatalog);

// ── 单字段选择器（仅在只需一个字段的场景使用） ──────────────────
export const useReaderTheme = () => useReaderStore(state => state.theme);
export const useReaderFontSize = () => useReaderStore(state => state.fontSize);
export const useReaderBrightness = () => useReaderStore(state => state.brightness);
export const useReaderDramaId = () => useReaderStore(state => state.dramaId);
export const useReaderPrmtCode = () => useReaderStore(state => state.prmtCode);
export const useReaderArticleUrl = () => useReaderStore(state => state.articleUrl);
export const useReaderInitReady = () => useReaderStore(state => state.initReady);
export const useReaderChapterDetailReady = () => useReaderStore(state => state.chapterDetailReady);
export const useReaderQueryAnalysisEnd = () => useReaderStore(state => state.queryAnalysisEnd);
export const useReaderBookDetail = () => useReaderStore(state => state.bookDetail);
export const useReaderUserAsset = () => useReaderStore(state => state.userAsset);
export const useReaderBookDetailPromise = () => useReaderStore(state => state.bookDetailPromise);
export const useReaderBookDetailReadyPromise = () => useReaderStore(state => state.bookDetailReadyPromise);
export const usePurchaseLoadingMap = () => useReaderStore(state => state.purchaseLoadingMap);
export const useBookDetailLoading = () => useReaderStore(state => state.bookDetailLoading);
export const usePurchaseResultMap = () => useReaderStore(state => state.purchaseResultMap);

/** 获取最近一次目录/菜单跳转的章节 ID */
export const useReaderJumpedChapterId = () => useReaderStore(state => state.jumpedChapterId);
